import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ShareIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Button } from './common';
import toast from 'react-hot-toast';

/**
 * Shareable Stats Card Generator
 * Creates a visually appealing card that users can download and share on LinkedIn/Twitter
 * This is a key viral mechanic - makes users WANT to share their success
 */
const ShareableCard = ({
  stats = {},
  userName = 'Job Seeker',
  variant = 'weekly',
  onShare
}) => {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const {
    jobsApplied = 0,
    interviews = 0,
    atsScore = 0,
    timeSaved = 0,
    jobsThisWeek = 0,
    tailoredResumes = 0
  } = stats;

  // Generate share text for clipboard
  const generateShareText = () => {
    if (variant === 'weekly') {
      return `My job search this week with Kwikly:

📊 ${jobsApplied} jobs applied
🎯 ${interviews} interviews scheduled
⏱️ ${timeSaved} hours saved

Try it free: kwikly.me

#JobSearch #CareerGrowth #Kwikly`;
    }

    if (variant === 'ats-score') {
      return `Just optimized my resume with Kwikly!

📈 ATS Score: ${atsScore}%
✅ ${tailoredResumes} resumes tailored

Stop getting filtered by ATS. Try it: kwikly.me

#Resume #ATS #JobSearch #Kwikly`;
    }

    if (variant === 'success') {
      return `Landed an interview at ${stats.company || 'my dream company'}! 🎉

My Kwikly journey:
📊 ${jobsApplied} applications
📈 ${atsScore}% ATS match
✅ Interview secured!

Try it: kwikly.me

#JobSearch #Success #Kwikly`;
    }

    return '';
  };

  // Download card as image
  const handleDownload = async () => {
    if (!cardRef.current) return;

    setDownloading(true);
    try {
      // Dynamic import html2canvas for code splitting
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
        logging: false,
        useCORS: true
      });

      const link = document.createElement('a');
      link.download = `kwikly-stats-${variant}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      toast.success('Card downloaded!');
    } catch (err) {
      console.error('Failed to generate image:', err);
      toast.error('Failed to download card');
    } finally {
      setDownloading(false);
    }
  };

  // Copy share text to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(generateShareText());
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Share via Web Share API if available
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Kwikly Stats',
          text: generateShareText(),
          url: 'https://kwikly.me'
        });
      } catch (err) {
        // User cancelled or share failed
        handleCopy();
      }
    } else {
      handleCopy();
    }
    onShare?.();
  };

  // Weekly stats card design
  const WeeklyCard = () => (
    <div
      ref={cardRef}
      className="w-[400px] bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-6 text-white shadow-xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-white/80 text-sm">This Week with</p>
          <h2 className="text-2xl font-bold">Kwikly</h2>
        </div>
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
          <span className="text-2xl">🚀</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/10 backdrop-blur rounded-xl p-4">
          <p className="text-4xl font-bold">{jobsApplied}</p>
          <p className="text-white/80 text-sm">Jobs Applied</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-xl p-4">
          <p className="text-4xl font-bold">{interviews}</p>
          <p className="text-white/80 text-sm">Interviews</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-xl p-4">
          <p className="text-4xl font-bold">{tailoredResumes}</p>
          <p className="text-white/80 text-sm">Resumes Tailored</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-xl p-4">
          <p className="text-4xl font-bold">{timeSaved}h</p>
          <p className="text-white/80 text-sm">Time Saved</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-white/20">
        <p className="text-white/60 text-sm">kwikly.me</p>
        <p className="text-white/80 text-sm">{userName}</p>
      </div>
    </div>
  );

  // ATS Score card design
  const ATSCard = () => (
    <div
      ref={cardRef}
      className="w-[400px] bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 rounded-2xl p-6 text-white shadow-xl"
    >
      {/* Header */}
      <div className="text-center mb-6">
        <p className="text-white/80 text-sm mb-1">My ATS Score</p>
        <h2 className="text-6xl font-bold">{atsScore}%</h2>
        <p className="text-white/90 mt-2">
          {atsScore >= 90 ? '🎉 Excellent Match!' :
           atsScore >= 80 ? '✨ Great Match!' :
           atsScore >= 70 ? '👍 Good Match' : '🔧 Room to Improve'}
        </p>
      </div>

      {/* Progress bar */}
      <div className="bg-white/20 rounded-full h-4 mb-6 overflow-hidden">
        <div
          className="bg-white h-full rounded-full transition-all duration-500"
          style={{ width: `${atsScore}%` }}
        />
      </div>

      {/* Stats */}
      <div className="flex justify-center gap-8 mb-6">
        <div className="text-center">
          <p className="text-3xl font-bold">{tailoredResumes}</p>
          <p className="text-white/80 text-sm">Resumes Tailored</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold">{jobsApplied}</p>
          <p className="text-white/80 text-sm">Jobs Applied</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-white/20">
        <p className="text-white/60 text-sm">Powered by Kwikly</p>
        <p className="text-white/80 text-sm">kwikly.me</p>
      </div>
    </div>
  );

  // Success celebration card
  const SuccessCard = () => (
    <div
      ref={cardRef}
      className="w-[400px] bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-xl"
    >
      {/* Header */}
      <div className="text-center mb-6">
        <span className="text-6xl mb-4 block">🎉</span>
        <h2 className="text-2xl font-bold">Interview Landed!</h2>
        {stats.company && (
          <p className="text-white/90 mt-2">at {stats.company}</p>
        )}
      </div>

      {/* Journey stats */}
      <div className="bg-white/20 backdrop-blur rounded-xl p-4 mb-6">
        <p className="text-white/80 text-sm mb-3">My Journey:</p>
        <div className="flex justify-around">
          <div className="text-center">
            <p className="text-2xl font-bold">{jobsApplied}</p>
            <p className="text-white/80 text-xs">Applications</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{atsScore}%</p>
            <p className="text-white/80 text-xs">ATS Match</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{timeSaved}h</p>
            <p className="text-white/80 text-xs">Saved</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pt-4 border-t border-white/20">
        <p className="text-white/80 text-sm">Your next interview awaits at kwikly.me</p>
      </div>
    </div>
  );

  // Render the appropriate card variant
  const renderCard = () => {
    switch (variant) {
      case 'ats-score':
        return <ATSCard />;
      case 'success':
        return <SuccessCard />;
      default:
        return <WeeklyCard />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Card Preview */}
      <div className="flex justify-center">
        {renderCard()}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-3">
        <Button
          variant="primary"
          onClick={handleDownload}
          loading={downloading}
        >
          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
          Download
        </Button>
        <Button
          variant="secondary"
          onClick={handleCopy}
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 mr-2" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
          )}
          {copied ? 'Copied!' : 'Copy Text'}
        </Button>
        <Button
          variant="secondary"
          onClick={handleShare}
        >
          <ShareIcon className="h-4 w-4 mr-2" />
          Share
        </Button>
      </div>

      {/* Share prompts */}
      <div className="text-center text-sm text-gray-500">
        <p>Share on LinkedIn or Twitter to inspire fellow job seekers!</p>
      </div>
    </div>
  );
};

ShareableCard.propTypes = {
  stats: PropTypes.shape({
    jobsApplied: PropTypes.number,
    interviews: PropTypes.number,
    atsScore: PropTypes.number,
    timeSaved: PropTypes.number,
    jobsThisWeek: PropTypes.number,
    tailoredResumes: PropTypes.number,
    company: PropTypes.string
  }),
  userName: PropTypes.string,
  variant: PropTypes.oneOf(['weekly', 'ats-score', 'success']),
  onShare: PropTypes.func
};

export default ShareableCard;
